/**
 * Email Journal PDF — portal-staff adapter (ADR-074 §3.5 req 8, Stage 6).
 *
 * Thin wrapper over the SHARED builder (@portal-shared/email-journal-pdf-core),
 * mirroring portal-subscriber's fix-guide-pdf.ts: it only supplies the
 * client-side jsPDF constructor. The OutboundEmailRow → EmailJournalEntry
 * projection lives here so the core stays free of the surface/lambda types.
 */
import { jsPDF } from 'jspdf';
import {
  buildEmailJournalPdfCore,
  type EmailJournalData,
  type EmailJournalEntry,
} from '@portal-shared/email-journal-pdf-core';
import type { OutboundEmailRow } from './email-validation-api';

export type { EmailJournalData, EmailJournalEntry };

/** Map the visible surface row onto the flat PDF entry shape. */
export function toJournalEntry(row: OutboundEmailRow): EmailJournalEntry {
  return {
    friendlyId: row.friendlyId,
    recipient: row.recipient,
    fromAddress: row.fromAddress,
    workflow: row.workflow,
    subject: row.subject,
    sesMessageId: row.sesMessageId,
    sentAt: row.sentAt,
    lastEventAt: row.lastEventAt,
    stateLabel: row.state.label,
    isIssue: row.state.isIssue,
    category: categoryLabel(row),
    transitions: row.transitions.map((t) => ({
      stage: t.stage,
      at: t.at,
      resent: t.event === 'resent' || undefined,
    })),
  };
}

/** The summary-tally category for a row — mirrors EmailValidationOutbound.categoryOf. */
function categoryLabel(row: OutboundEmailRow): string {
  switch (row.effectiveStage) {
    case 'Bounced':
      return 'Bounced';
    case 'Complaint':
      return 'Complaint';
    case 'Rejected':
      return 'Rejected';
    case 'Closed':
    case 'Read':
      return 'Closed';
    case 'Delivered':
      return 'Delivered';
    case 'Sent':
      return row.state.label === 'In Limbo' ? 'In Limbo' : 'Sent';
    default:
      return 'Sent';
  }
}

/** Build the Email Journal export from the currently-visible rows. */
export function buildEmailJournalPdf(rows: OutboundEmailRow[], scopeLabel: string, generatedDate: string): jsPDF {
  const data: EmailJournalData = {
    generatedDate,
    scopeLabel,
    entries: rows.map(toJournalEntry),
  };
  return buildEmailJournalPdfCore(() => new jsPDF({ unit: 'mm', format: 'a4' }), data) as jsPDF;
}
